import React, {
  createContext,
  useContext,
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const { accessToken } = useAuth();

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const connectionTimeoutRef = useRef(null);
  const connectFunctionRef = useRef(null);
  const isComponentMountedRef = useRef(true);
  const isConnectingRef = useRef(false);
  const intentionalCloseRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);

  const [status, setStatus] = useState("desconectado");
  const [isConnecting, setIsConnecting] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [messages, setMessages] = useState([]);

  // Configurações
  const maxReconnectAttempts = Math.max(
    Number(process.env.REACT_APP_WS_BACKOFF_ATTEMPTS) || 12,
    1,
  );
  const websocketUrl = process.env.REACT_APP_WS_URL?.replace(
    /^http(s?):/,
    (_, secure) => (secure ? "wss:" : "ws:"),
  );

  // Adicionar mensagem ao log
  const addMessage = useCallback((data) => {
    const messageWithId = {
      ...data,
      id: Date.now() + Math.random(),
      receivedAt: new Date().toLocaleTimeString("pt-BR"),
    };
    setMessages((prev) => [messageWithId, ...prev.slice(0, 99)]);
  }, []);

  // Limpar mensagens
  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  // Função para enviar ações
  const sendAction = useCallback((action, payload = {}) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action, ...payload }));
      return true;
    } else {
      setStatus("erro: não conectado ao servidor");
      return false;
    }
  }, []);

  const clearConnectionTimeout = useCallback(() => {
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
  }, []);

  const attemptReconnect = useCallback(() => {
    if (
      !isComponentMountedRef.current ||
      intentionalCloseRef.current ||
      reconnectTimeoutRef.current
    ) {
      return;
    }

    const nextAttempt = reconnectAttemptsRef.current + 1;
    reconnectAttemptsRef.current = nextAttempt;
    setReconnectAttempts(nextAttempt);

    const backoffAttempt = Math.min(nextAttempt, maxReconnectAttempts);
    const baseDelay = Math.min(1000 * 2 ** (backoffAttempt - 1), 30000);
    const reconnectDelay = Math.round(baseDelay * (0.8 + Math.random() * 0.4));

    setStatus(
      `reconectando (tentativa ${nextAttempt}) em ${Math.ceil(
        reconnectDelay / 1000,
      )}s...`,
    );

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      connectFunctionRef.current?.();
    }, reconnectDelay);
  }, [maxReconnectAttempts]);

  const connectWebSocket = useCallback(() => {
    if (
      !isComponentMountedRef.current ||
      intentionalCloseRef.current ||
      isConnectingRef.current ||
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    if (!websocketUrl) {
      setStatus("erro: URL do WebSocket não configurada");
      return;
    }

    isConnectingRef.current = true;
    setIsConnecting(true);
    setStatus("conectando ao servidor...");

    try {
      if (!accessToken) {
        isConnectingRef.current = false;
        setIsConnecting(false);
        setStatus("aguardando autenticação...");
        return;
      }

      const ws = new WebSocket(websocketUrl, [`jwt.${accessToken}`]);
      wsRef.current = ws;

      connectionTimeoutRef.current = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          ws.close(4000, "Timeout ao conectar");
        }
      }, 15000);

      ws.onopen = () => {
        if (!isComponentMountedRef.current || wsRef.current !== ws) return;

        clearConnectionTimeout();
        isConnectingRef.current = false;
        reconnectAttemptsRef.current = 0;
        setIsConnecting(false);
        setReconnectAttempts(0);
        setStatus("conectado ao servidor");
        ws.send(JSON.stringify({ action: "initialize_whatsapp" }));
      };

      ws.onmessage = (event) => {
        if (!isComponentMountedRef.current || wsRef.current !== ws) return;

        try {
          addMessage(JSON.parse(event.data));
        } catch (error) {
          console.error("Erro ao processar mensagem WebSocket:", error);
        }
      };

      ws.onclose = (event) => {
        if (wsRef.current === ws) {
          wsRef.current = null;
        }

        clearConnectionTimeout();
        isConnectingRef.current = false;
        setIsConnecting(false);

        if (!isComponentMountedRef.current || intentionalCloseRef.current) {
          return;
        }

        console.warn(
          `WebSocket desconectado: ${event.code} - ${event.reason}`,
        );
        setStatus("desconectado do servidor");
        attemptReconnect();
      };

      ws.onerror = () => {
        if (!isComponentMountedRef.current || wsRef.current !== ws) return;
        setStatus("erro de conexão");
      };
    } catch (error) {
      clearConnectionTimeout();
      isConnectingRef.current = false;
      setIsConnecting(false);
      setStatus("erro ao criar conexão");
      attemptReconnect();
    }
  }, [
    addMessage,
    accessToken,
    attemptReconnect,
    clearConnectionTimeout,
    websocketUrl,
  ]);

  connectFunctionRef.current = connectWebSocket;

  // Reconectar manualmente
  const manualReconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    reconnectAttemptsRef.current = 0;
    setReconnectAttempts(0);

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    clearConnectionTimeout();
    wsRef.current?.close(1000, "Reconexão manual");

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      intentionalCloseRef.current = false;
      connectFunctionRef.current?.();
    }, 300);
  }, [clearConnectionTimeout]);

  // Conectar na inicialização
  useEffect(() => {
    isComponentMountedRef.current = true;
    intentionalCloseRef.current = false;

    const timeout = setTimeout(() => {
      connectFunctionRef.current?.();
    }, 300);

    const reconnectNow = () => {
      if (
        !isComponentMountedRef.current ||
        wsRef.current?.readyState === WebSocket.OPEN
      ) {
        return;
      }

      intentionalCloseRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      connectFunctionRef.current?.();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") reconnectNow();
    };

    window.addEventListener("online", reconnectNow);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isComponentMountedRef.current = false;
      intentionalCloseRef.current = true;
      clearTimeout(timeout);
      clearConnectionTimeout();

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      window.removeEventListener("online", reconnectNow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      wsRef.current?.close(1000, "App desmontado");
      wsRef.current = null;
    };
  }, [clearConnectionTimeout]);

  const value = {
    ws: wsRef.current,
    status,
    isConnecting,
    reconnectAttempts,
    messages,
    sendAction,
    manualReconnect,
    clearMessages,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket deve ser usado dentro de WebSocketProvider");
  }
  return context;
};
